import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { ViewerHost, type EngineId } from '@geosoft/viewer-sdk';
import { CatalogClient } from '@geosoft/catalog-sdk';
import { initAuth, getAccessToken, getCurrentUser } from '@geosoft/platform-sdk';
import { MapLibreAdapter } from '@/engines/maplibre/MapLibreAdapter';
import { DeckGLAdapter } from '@/engines/deckgl/DeckGLAdapter';
import {
  CesiumAdapter,
  type CesiumPerformanceSnapshot,
} from '@/engines/cesium/CesiumAdapter';
import { ThreeAdapter } from '@/engines/three/ThreeAdapter';
import { Giro3DAdapter } from '@/engines/giro3d/Giro3DAdapter';
import { PotreeAdapter } from '@/engines/potree/PotreeAdapter';
import { useViewerStore } from '@/state/useViewerStore';
import { usePluginStore, type EngineType as PluginEngineType } from '@/state/usePluginStore';
import { LayerPanel } from '@/components/layers/LayerPanel';
import { CopilotDock } from '@/components/copilot/CopilotDock';
import { TimelineScrubber } from '@/components/timeline/TimelineScrubber';
import { PluginDock } from '@/components/plugins/PluginDock';
import {
  Stack,
  useDebounced,
  useDefenceVariant,
  tokens,
} from '@/design';
import { useLiveRegion } from '@/state/liveRegion';
import { useDrawerHost } from '@/state/drawerHost';
import { selectionBus } from '@/state/selectionBus';
import { ShellBottomBar } from './ShellBottomBar';
import { ShellDrawers } from './ShellDrawers';
import { ShellHeader, type ShellDrawerId } from './ShellHeader';
import { ShellRightPanel } from './ShellRightPanel';
import { ShellWelcomeCard } from './ShellWelcomeCard';
import { DefenceChip } from '@/components/chrome/DefenceChip';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { FloatingMapToolbar } from '@/components/map/FloatingMapToolbar';
import { MapSearchResults } from '@/components/map/MapSearchResults';
import { MapClusterOverlay } from '@/components/map/MapClusterOverlay';
import { MapStateOverlays } from '@/components/map/MapStateOverlays';
import {
  bboxToCamera,
  combinedBbox,
  deriveMapStateFlags,
  visibleLayers,
} from '@/components/map/mapModel';
import {
  buildThreeDHostSummary,
  buildThreeDObjectNodes,
  type ThreeDObjectNode,
  type ThreeDPerformanceSnapshot,
} from '@/components/three-d/model';
import { ThreeDToolbar } from '@/components/three-d/ThreeDToolbar';
import { ThreeDCompass } from '@/components/three-d/ThreeDCompass';
import { ThreeDObjectTree } from '@/components/three-d/ThreeDObjectTree';
import { ThreeDControlsPanel } from '@/components/three-d/ThreeDControlsPanel';
import { ThreeDPerformanceChip } from '@/components/three-d/ThreeDPerformanceChip';
import { MissingGeometryDiagnostics } from '@/components/three-d/MissingGeometryDiagnostics';
import { ThreeDCanvasHost } from '@/components/three-d/ThreeDCanvasHost';
import { PointCloudPaneHost } from '@/components/pointcloud/PointCloudPaneHost';
import { PointCloudColorMode } from '@/components/pointcloud/PointCloudColorMode';
import { PointCloudSliceTool } from '@/components/pointcloud/PointCloudSliceTool';
import { PointCloudDensityControls } from '@/components/pointcloud/PointCloudDensityControls';
import { PointCloudRegistrationDiagnostics } from '@/components/pointcloud/PointCloudRegistrationDiagnostics';
import {
  isPointCloudLayer,
  resolvePointCloudRoute,
  type PointCloudPerformanceSnapshot,
} from '@/components/pointcloud/model';
import { providerIdFromLayer } from '@/components/layers/layerModel';
import { ProfileCanvas } from '@/components/profile';

/**
 * WP-11 / masterplan §10.6 ("Shell discipline"): `AppShell` MUST NOT
 * be mounted under the gTwin Twin route (`/t/:gtwinId`). The Twin
 * route uses `<TwinShell />` directly via `<TwinRoute />`. Mounting
 * AppShell there would re-bootstrap the catalog from
 * `CatalogClient.search()` and override the gTwin-hydrated layers,
 * defeating the gTwin-first workflow.
 *
 * The route table in `frontend/src/app/routes.tsx` already enforces
 * this (only `<WorkspaceShell />` mounts `<AppShell />`). The dev-time
 * guard below documents the rule in code and surfaces a console
 * error if a future change accidentally relaxes the routing.
 */
function assertNotOnTwinRoute(): void {
  if (typeof window === 'undefined') return;
  const path = window.location?.pathname ?? '';
  if (path.startsWith('/t/')) {
    if (typeof console !== 'undefined') {
      console.error(
        '[geosoft] <AppShell /> mounted under a Twin route ' +
          `(${path}). The Twin route must use <TwinShell /> only — see ` +
          'docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md §10.6.',
      );
    }
  }
}

export function AppShell() {
  assertNotOnTwinRoute();
  const mapContainer = useRef<HTMLDivElement>(null);
  const hostRef = useRef<ViewerHost | null>(null);
  const cesiumAdapterRef = useRef<CesiumAdapter | null>(null);
  const threeAdapterRef = useRef<ThreeAdapter | null>(null);
  const lastMainLabelAnnouncedRef = useRef('');
  const [engine, setEngine] = useState<EngineId>('maplibre');
  const [activeDrawer, setActiveDrawer] = useState<ShellDrawerId | null>(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('gvw-027-dismissed') === '1';
  });
  const setCatalog = useViewerStore((s) => s.setCatalog);
  const setCamera = useViewerStore((s) => s.setCamera);
  const setVisible = useViewerStore((s) => s.setVisible);
  const setSelection = useViewerStore((s) => s.setSelection);
  const selection = useViewerStore((s) => s.selection);
  const camera = useViewerStore((s) => s.camera);
  const visible = useViewerStore((s) => s.visibleLayerIds);
  const catalog = useViewerStore((s) => s.catalog);
  const time = useViewerStore((s) => s.time);
  const setPluginEngineType = usePluginStore((s) => s.setEngineType);
  const setPluginEngineReady = usePluginStore((s) => s.setEngineReady);
  const mapTool = useViewerStore((s) => s.mapTool);
  const setMapTool = useViewerStore((s) => s.setMapTool);
  const threeDCameraMode = useViewerStore((s) => s.threeDCameraMode);
  const setThreeDCameraMode = useViewerStore((s) => s.setThreeDCameraMode);
  const threeDCanvasTone = useViewerStore((s) => s.threeDCanvasTone);
  const setThreeDCanvasTone = useViewerStore((s) => s.setThreeDCanvasTone);
  const threeDExplodeFactor = useViewerStore((s) => s.threeDExplodeFactor);
  const setThreeDExplodeFactor = useViewerStore((s) => s.setThreeDExplodeFactor);
  const pointCloudColorMode = useViewerStore((s) => s.pointCloudColorMode);
  const setPointCloudColorMode = useViewerStore((s) => s.setPointCloudColorMode);
  const pointCloudSliceOffset = useViewerStore((s) => s.pointCloudSliceOffset);
  const setPointCloudSliceOffset = useViewerStore((s) => s.setPointCloudSliceOffset);
  const pointCloudDensityTarget = useViewerStore((s) => s.pointCloudDensityTarget);
  const setPointCloudDensityTarget = useViewerStore((s) => s.setPointCloudDensityTarget);
  const pointCloudRegistrationState = useViewerStore((s) => s.pointCloudRegistrationState);
  const setPointCloudRegistrationState = useViewerStore((s) => s.setPointCloudRegistrationState);
  const permissionDeniedMessage = useViewerStore((s) => s.permissionDeniedMessage);
  const mode = useViewerStore((s) => s.mode);
  const setPermissionDeniedMessage = useViewerStore((s) => s.setPermissionDeniedMessage);
  const clearPermissionDeniedMessage = useViewerStore((s) => s.clearPermissionDeniedMessage);
  const [threeDControlState, setThreeDControlState] = useState({
    isolated: false,
    hidden: false,
    ghosted: false,
    sectionPlaneEnabled: false,
    clipBoxEnabled: false,
    lightingEnabled: true,
    shadowsEnabled: true,
    sunHour: 12,
    sectionOffset: 0,
    clipOffset: 0,
    materialState: 'textured' as 'textured' | 'untextured' | 'missing-texture',
  });
  const [threeDPerformance, setThreeDPerformance] = useState<ThreeDPerformanceSnapshot>({
    fps: 60,
    lod: 'balanced',
    pointBudget: 0,
    droppedFrames: 0,
  });
  const [pointCloudPerformance, setPointCloudPerformance] =
    useState<PointCloudPerformanceSnapshot>({
      pointBudget: 0,
      loadedTiles: 0,
      failedTiles: 0,
    });
  const defenceVariant = useDefenceVariant();
  const liveRegion = useLiveRegion();
  const drawerHost = useDrawerHost();

  // One-time bootstrap: auth + viewer + catalog.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      await initAuth({
        keycloakUrl: import.meta.env.VITE_KEYCLOAK_URL ?? 'http://localhost:8080',
        realm: import.meta.env.VITE_KEYCLOAK_REALM ?? 'geosoft-dev',
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'geoviewer-web',
        defaultTenant: import.meta.env.VITE_DEFAULT_TENANT ?? 'tnt-default',
      });

      if (!mapContainer.current || cancelled) return;

      const adapters = (() => {
        switch (engine) {
          case 'cesium': {
            const cesium = new CesiumAdapter();
            const three = new ThreeAdapter();
            cesiumAdapterRef.current = cesium;
            threeAdapterRef.current = three;
            return [cesium, three];
          }
          case 'giro3d': {
            const giro3d = new Giro3DAdapter();
            cesiumAdapterRef.current = null;
            threeAdapterRef.current = null;
            return [giro3d, new MapLibreAdapter()];
          }
          case 'potree': {
            const potree = new PotreeAdapter();
            cesiumAdapterRef.current = null;
            threeAdapterRef.current = null;
            return [potree, new CesiumAdapter()];
          }
          case 'three': {
            const three = new ThreeAdapter();
            const cesium = new CesiumAdapter();
            cesiumAdapterRef.current = cesium;
            threeAdapterRef.current = three;
            return [cesium, three];
          }
          default: {
            cesiumAdapterRef.current = null;
            threeAdapterRef.current = null;
            return [new MapLibreAdapter(), new DeckGLAdapter()];
          }
        }
      })();

      const host = new ViewerHost({
        container: mapContainer.current,
        primaryEngine: engine,
        adapters,
      });
      await host.mount();
      hostRef.current = host;
      host.on((event) => {
        if (event.type === 'cameraChange') setCamera(event.camera);
        if (event.type === 'select') setSelection(event.selection);
      });
      // Plugin store gates measurement / profile / viewshed availability
      // on engineType + engineReady. Publish them whenever the host is
      // ready so the toolbar buttons become enabled.
      const pluginEngineType: PluginEngineType =
        engine === 'maplibre'
          ? 'maplibre'
          : engine === 'cesium'
            ? 'cesium'
            : engine === 'three'
              ? 'three'
              : engine === 'deckgl'
                ? 'deck'
                : null;
      setPluginEngineType(pluginEngineType);
      setPluginEngineReady(true);

      const catalogClient = new CatalogClient({
        baseUrl: import.meta.env.VITE_API_URL ?? 'http://localhost:3001',
        tenantId: import.meta.env.VITE_DEFAULT_TENANT ?? 'tnt-default',
        getToken: getAccessToken,
      });
      const layers = await catalogClient.search();
      if (!cancelled) setCatalog(layers);
    })();

    return () => {
      cancelled = true;
      setPluginEngineReady(false);
      setPluginEngineType(null);
      hostRef.current?.unmount();
      hostRef.current = null;
      cesiumAdapterRef.current = null;
      threeAdapterRef.current = null;
    };
  }, [engine, setCatalog, setCamera, setSelection, setPluginEngineType, setPluginEngineReady]);

  useEffect(
    () =>
      selectionBus.subscribe((event) => {
        const first = event.features[0];
        if (!first) return;
        setSelection({
          layerId: first.layerId,
          featureId: first.featureId,
        });
        if (event.source === 'host' && threeAdapterRef.current) {
          threeAdapterRef.current.setSelectedObjectId(`${first.layerId}:${String(first.featureId)}`);
        }
      }),
    [setSelection],
  );

  // Sync visible layers with the host whenever the user toggles them.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const present = new Set(host.listLayers().map((l) => l.id));
    for (const id of visible) {
      if (!present.has(id)) {
        const layer = catalog.find((l) => l.id === id);
        if (layer) {
          const providerId = providerIdFromLayer(layer);
          if (defenceVariant.active && !defenceVariant.allowProvider(providerId)) {
            setPermissionDeniedMessage(`Provider "${providerId}" is disabled in defence variant.`);
            continue;
          }
          void host.addLayer(layer).catch((err: unknown) => {
            console.warn(`[viewer] layer ${id} unsupported by ${engine}`, err);
          });
        }
      }
    }
    for (const id of present) {
      if (!visible.has(id)) void host.removeLayer(id);
    }
  }, [visible, catalog, defenceVariant, engine, setPermissionDeniedMessage]);

  // F5 timeline propagation: keep active engines synchronized to one timeline value.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.setTime(time ?? new Date());
  }, [time]);

  useEffect(() => {
    if (engine !== 'cesium') return;
    const timer = window.setInterval(() => {
      const cesium = cesiumAdapterRef.current;
      const snapshot: CesiumPerformanceSnapshot | null = cesium
        ? cesium.getPerformanceSnapshot()
        : null;
      if (snapshot) {
        setThreeDPerformance({
          fps: snapshot.fps,
          lod: snapshot.lod,
          pointBudget: snapshot.pointBudget,
          droppedFrames: snapshot.droppedFrames,
        });
        setPointCloudPerformance((previous) => ({
          pointBudget: snapshot.pointBudget,
          loadedTiles: Math.max(previous.loadedTiles, visible.size),
          failedTiles: snapshot.droppedFrames > 20 ? 1 : 0,
        }));
      }
    }, 750);
    return () => window.clearInterval(timer);
  }, [engine, visible.size]);

  const visibleLayerCount = visible.size;
  const isThreeDMode = engine === 'cesium';
  const userName = (() => {
    try {
      return getCurrentUser().displayName;
    } catch {
      return '...';
    }
  })();
  const isMayaFirstSession =
    userName.toLowerCase().includes('maya') &&
    !welcomeDismissed &&
    visibleLayerCount === 0;

  const crs = useMemo(() => {
    for (const layer of catalog) {
      if (visible.has(layer.id)) return layer.crs;
    }
    return 'EPSG:4326';
  }, [catalog, visible]);

  const activeLayers = useMemo(() => visibleLayers(catalog, visible), [catalog, visible]);
  const pointCloudLayers = useMemo(
    () => activeLayers.filter((layer) => isPointCloudLayer(layer)),
    [activeLayers],
  );
  const activePointCloudLayer = pointCloudLayers[0] ?? null;
  const pointCloudRoute = useMemo(
    () =>
      resolvePointCloudRoute({
        layer: activePointCloudLayer,
        defenceVariantActive: defenceVariant.active,
        pluginAllowed: defenceVariant.allowProvider('pointcloud-plugin'),
      }),
    [activePointCloudLayer, defenceVariant],
  );
  const isPointCloudMode = isThreeDMode && pointCloudLayers.length > 0;
  const threeDNodes = useMemo<ThreeDObjectNode[]>(
    () => buildThreeDObjectNodes(catalog, visible),
    [catalog, visible],
  );
  const missingGeometryCount = useMemo(
    () => threeDNodes.filter((node) => node.hasMissingGeometry).length,
    [threeDNodes],
  );
  const toneAvailable = tokens.color.canvas.threeDNeutral != null;
  const effectiveThreeDCanvasTone =
    isThreeDMode && toneAvailable ? threeDCanvasTone : 'light';
  const canvasBackground =
    isThreeDMode && effectiveThreeDCanvasTone === 'neutral-dark' && tokens.color.canvas.threeDNeutral
      ? tokens.color.canvas.threeDNeutral
      : tokens.color.canvas.light ?? tokens.color.surface[2];
  const mapFlags = useMemo(
    () => deriveMapStateFlags(activeLayers, permissionDeniedMessage),
    [activeLayers, permissionDeniedMessage],
  );
  const selectedLayer = useMemo(
    () => catalog.find((layer) => layer.id === selection?.layerId) ?? null,
    [catalog, selection?.layerId],
  );
  const mapDescription = useMemo(() => {
    const layerNames = activeLayers.map((layer) => layer.label ?? layer.id).slice(0, 5);
    const nameSummary = layerNames.length > 0 ? layerNames.join(', ') : 'none';
    const centerSummary =
      camera != null
        ? `${camera.lonLat[1].toFixed(4)}, ${camera.lonLat[0].toFixed(4)}`
        : 'unavailable';
    const zoomSummary = camera?.zoom != null ? camera.zoom.toFixed(2) : 'n/a';
    return `Map of ${centerSummary}; zoom ${zoomSummary}; CRS ${crs}; ${visibleLayerCount} layers visible: ${nameSummary}.`;
  }, [activeLayers, camera, crs, visibleLayerCount]);
  const threeDHostSummary = useMemo(
    () =>
      buildThreeDHostSummary({
        selection,
        cameraMode: threeDCameraMode,
        objectCount: threeDNodes.length,
        performance: threeDPerformance,
        canvasTone: effectiveThreeDCanvasTone,
      }),
    [
      effectiveThreeDCanvasTone,
      selection,
      threeDCameraMode,
      threeDNodes.length,
      threeDPerformance,
    ],
  );
  const pointCloudDescription = useMemo(() => {
    const mode = pointCloudColorMode.toUpperCase();
    return `Point cloud mode. Route ${pointCloudRoute}. Color mode ${mode}. Point budget ${Math.round(
      pointCloudPerformance.pointBudget,
    )}.`;
  }, [pointCloudColorMode, pointCloudPerformance.pointBudget, pointCloudRoute]);
  const pointCloudNarration = useMemo(
    () =>
      `Registration state ${pointCloudRegistrationState}. Slice ${Math.round(
        pointCloudSliceOffset * 100,
      )} percent. Density target ${Math.round(pointCloudDensityTarget * 100)} percent.`,
    [pointCloudDensityTarget, pointCloudRegistrationState, pointCloudSliceOffset],
  );
  const mapStateNarration = useMemo(() => {
    const states: string[] = [];
    if (mapFlags.offline) states.push('Offline tile mode active.');
    if (mapFlags.stale || mapFlags.partialLoading) states.push('Some tiles are stale or partial.');
    if (mapFlags.lowAccuracy) states.push('Low coordinate accuracy detected.');
    if (mapFlags.mixedCrs) states.push('Mixed CRS layers are visible.');
    if (mapFlags.expiredCredentials) states.push('At least one connector has expired credentials.');
    if (permissionDeniedMessage) states.push(permissionDeniedMessage);
    return states.join(' ');
  }, [mapFlags, permissionDeniedMessage]);

  const mainAriaLabel = useMemo(() => {
    const modeLabel = isPointCloudMode
      ? 'point cloud mode'
      : isThreeDMode
        ? '3D scene mode'
        : 'map mode';
    const base = `${modeLabel} using ${engine}; ${visibleLayerCount} visible layer${visibleLayerCount === 1 ? '' : 's'}`;
    if (!camera) return `${base}; camera pending`;
    const [lon, lat] = camera.lonLat;
    const zoom = camera.zoom != null ? ` zoom ${camera.zoom.toFixed(2)}` : '';
    return `${base}; center ${lon.toFixed(4)}, ${lat.toFixed(4)}${zoom}`;
  }, [camera, engine, isPointCloudMode, isThreeDMode, visibleLayerCount]);

  const canvasDescription = isPointCloudMode
    ? pointCloudDescription
    : isThreeDMode
      ? threeDHostSummary.description
      : mapDescription;
  const canvasNarration = isPointCloudMode
    ? pointCloudNarration
    : isThreeDMode
      ? threeDHostSummary.narration
      : mapStateNarration;

  const debouncedMainAriaLabel = useDebounced(mainAriaLabel, 500);
  const debouncedCanvasDescription = useDebounced(canvasDescription, 1000);
  const debouncedStateNarration = useDebounced(canvasNarration, 500);

  useEffect(() => {
    if (lastMainLabelAnnouncedRef.current === debouncedMainAriaLabel) return;
    liveRegion.announce('status', debouncedMainAriaLabel);
    lastMainLabelAnnouncedRef.current = debouncedMainAriaLabel;
  }, [debouncedMainAriaLabel, liveRegion]);

  const closeActiveDrawer = useCallback(() => {
    if (!activeDrawer) return;
    drawerHost.close(activeDrawer);
    setActiveDrawer(null);
  }, [activeDrawer, drawerHost]);

  const openDrawer = useCallback(
    (id: ShellDrawerId, opener: HTMLElement | null) => {
      drawerHost.open(id, () =>
        setActiveDrawer((current) => (current === id ? null : current)),
      );
      setActiveDrawer(id);
      if (opener) opener.focus();
    },
    [drawerHost],
  );

  const dismissWelcome = useCallback(() => {
    setWelcomeDismissed(true);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('gvw-027-dismissed', '1');
    }
  }, []);

  const onMapFocus = useCallback(() => {
    if (isPointCloudMode) {
      liveRegion.announce('status', 'Point cloud keyboard mode active. Press question mark for shortcut help.');
      return;
    }
    if (isThreeDMode) {
      liveRegion.announce('status', '3D keyboard mode active. Press question mark for shortcut help.');
      return;
    }
    liveRegion.announce('status', 'Map keyboard mode active. Press question mark for shortcut help.');
  }, [isPointCloudMode, isThreeDMode, liveRegion]);

  const fitToVisibleLayers = useCallback(() => {
    const cameraTarget = bboxToCamera(combinedBbox(activeLayers));
    if (!cameraTarget) return;
    const host = hostRef.current;
    host?.flyTo(cameraTarget);
    setCamera({
      lonLat: cameraTarget.lonLat,
      zoom: cameraTarget.zoom,
    });
  }, [activeLayers, setCamera]);

  const flyToLayer = useCallback(
    (layerId: string) => {
      const layer = catalog.find((candidate) => candidate.id === layerId);
      if (!layer) return;
      const cameraTarget = bboxToCamera(layer.bbox);
      if (!cameraTarget) return;
      hostRef.current?.flyTo(cameraTarget);
      setSelection({ layerId: layer.id });
      setCamera({
        lonLat: cameraTarget.lonLat,
        zoom: cameraTarget.zoom,
      });
    },
    [catalog, setCamera, setSelection],
  );

  const adjustZoom = useCallback(
    (delta: number) => {
      if (!camera) return;
      const currentZoom = camera.zoom ?? 8;
      const nextZoom = Math.max(2, Math.min(20, currentZoom + delta));
      const next = { lonLat: camera.lonLat, zoom: nextZoom };
      hostRef.current?.flyTo(next);
      setCamera(next);
    },
    [camera, setCamera],
  );

  const panCamera = useCallback(
    (lonStep: number, latStep: number) => {
      if (!camera) return;
      const zoom = camera.zoom ?? 8;
      const divider = Math.max(1, zoom);
      const next = {
        lonLat: [camera.lonLat[0] + lonStep / divider, camera.lonLat[1] + latStep / divider] as [
          number,
          number,
        ],
        zoom,
      };
      hostRef.current?.flyTo(next);
      setCamera(next);
    },
    [camera, setCamera],
  );

  const focusLayers = useCallback(() => {
    const layerNode = document.getElementById('layers');
    layerNode?.focus();
  }, []);

  const handleMapKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const isTypingTarget = target.closest('input,textarea,select,[contenteditable="true"]');
      if (isTypingTarget) return;
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        panCamera(0, 1);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        panCamera(0, -1);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        panCamera(-1, 0);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        panCamera(1, 0);
        return;
      }
      switch (event.key) {
        case '+':
        case '=':
          event.preventDefault();
          adjustZoom(1);
          return;
        case '-':
        case '_':
          event.preventDefault();
          adjustZoom(-1);
          return;
        case 'f':
        case 'F':
          event.preventDefault();
          if (isThreeDMode) {
            setThreeDCameraMode('look-at-selected');
            liveRegion.announce('status', 'Camera set to look at selected object.');
          } else {
            fitToVisibleLayers();
          }
          return;
        case 'm':
        case 'M':
          event.preventDefault();
          setMapTool('measure');
          return;
        case 'a':
        case 'A':
          event.preventDefault();
          setMapTool('area');
          return;
        case 'p':
        case 'P':
          event.preventDefault();
          if (isThreeDMode) {
            setThreeDCameraMode('pan');
          } else {
            setMapTool('pan');
          }
          return;
        case 's':
        case 'S':
          event.preventDefault();
          if (isPointCloudMode) {
            setPointCloudSliceOffset(pointCloudSliceOffset === 0 ? 0.25 : 0);
          } else if (isThreeDMode) {
            setThreeDControlState((previous) => ({
              ...previous,
              sectionPlaneEnabled: !previous.sectionPlaneEnabled,
            }));
          } else {
            setMapTool('select');
          }
          return;
        case 'l':
        case 'L':
          event.preventDefault();
          if (isThreeDMode) {
            setThreeDCameraMode('look-at-selected');
          } else {
            focusLayers();
          }
          return;
        case 'i':
        case 'I':
          if (!isThreeDMode) return;
          event.preventDefault();
          setThreeDControlState((previous) => ({ ...previous, isolated: !previous.isolated }));
          return;
        case 'g':
        case 'G':
          if (!isThreeDMode) return;
          event.preventDefault();
          setThreeDControlState((previous) => ({ ...previous, ghosted: !previous.ghosted }));
          return;
        case 'r':
        case 'R':
          if (!isThreeDMode) return;
          event.preventDefault();
          setThreeDCameraMode('orbit');
          fitToVisibleLayers();
          return;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          if (!isPointCloudMode) return;
          event.preventDefault();
          if (event.key === '1') setPointCloudColorMode('rgb');
          if (event.key === '2') setPointCloudColorMode('elevation');
          if (event.key === '3') setPointCloudColorMode('intensity');
          if (event.key === '4') setPointCloudColorMode('classification');
          if (event.key === '5') setPointCloudColorMode('deviation');
          return;
        default:
          return;
      }
    },
    [
      adjustZoom,
      fitToVisibleLayers,
      focusLayers,
      isPointCloudMode,
      isThreeDMode,
      liveRegion,
      panCamera,
      pointCloudSliceOffset,
      setMapTool,
      setPointCloudColorMode,
      setPointCloudSliceOffset,
      setThreeDCameraMode,
    ],
  );

  const handleShellKeyDownCapture = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape') return;

      if (mapTool !== 'select') {
        setMapTool('select');
        liveRegion.announce('status', 'Map tool cleared.');
        event.preventDefault();
        return;
      }

      if (isThreeDMode && threeDCameraMode !== 'orbit') {
        setThreeDCameraMode('orbit');
        liveRegion.announce('status', 'Camera mode reset to orbit.');
        event.preventDefault();
        return;
      }

      if (selection) {
        setSelection(null);
        liveRegion.announce('status', 'Selection cleared.');
        event.preventDefault();
        return;
      }

      if (activeDrawer) {
        closeActiveDrawer();
        liveRegion.announce('status', 'Drawer closed.');
        event.preventDefault();
      }
    },
    [
      activeDrawer,
      closeActiveDrawer,
      isThreeDMode,
      liveRegion,
      mapTool,
      selection,
      setMapTool,
      setSelection,
      setThreeDCameraMode,
      threeDCameraMode,
    ],
  );

  const handleSelectThreeDNode = useCallback(
    (node: ThreeDObjectNode) => {
      setSelection({ layerId: node.layerId, featureId: node.id });
      if (node.visible) flyToLayer(node.layerId);
      if (threeAdapterRef.current) {
        threeAdapterRef.current.setSelectedObjectId(node.id);
      }
    },
    [flyToLayer, setSelection],
  );

  const handleToggleThreeDNodeVisibility = useCallback(
    (node: ThreeDObjectNode) => {
      setVisible(node.layerId, !node.visible);
      setThreeDControlState((previous) => ({ ...previous, hidden: node.visible }));
    },
    [setVisible],
  );

  const handleResetNorth = useCallback(() => {
    setThreeDCameraMode('orbit');
    fitToVisibleLayers();
  }, [fitToVisibleLayers, setThreeDCameraMode]);

  const supplementalBottomStatus = isThreeDMode ? (
    <ThreeDPerformanceChip snapshot={threeDPerformance} pointCloudMode={isPointCloudMode} />
  ) : null;

  return (
    <div
      className="app-shell"
      onKeyDownCapture={handleShellKeyDownCapture}
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        height: '100vh',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
      }}
    >
      <ShellHeader
        engine={engine}
        onEngineChange={setEngine}
        userName={userName}
        defenceVariantActive={defenceVariant.active}
        compareDisabled={visibleLayerCount < 2}
        shareDisabled={visibleLayerCount === 0}
        onOpenDrawer={openDrawer}
      />

      <NotificationCenter />

      <DefenceChip />

      <div
        className="app-main"
        style={{
          display: 'grid',
          gridTemplateColumns: `${tokens.layout.leftRail}px minmax(0, 1fr) ${tokens.layout.rightPanel}px ${tokens.layout.rightPanel}px`,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <nav
          id="layers"
          role="navigation"
          aria-label="Layers and saved views"
          tabIndex={-1}
          style={{
            minHeight: 0,
            borderRight: tokens.border.thin,
            background: tokens.color.surface[1],
          }}
        >
          <LayerPanel onFlyToLayer={(layer) => flyToLayer(layer.id)} />
          {isThreeDMode ? (
            <ThreeDObjectTree
              nodes={threeDNodes}
              selectedLayerId={selection?.layerId}
              onSelectNode={handleSelectThreeDNode}
              onToggleVisibility={handleToggleThreeDNodeVisibility}
            />
          ) : null}
        </nav>

        <main
          role="main"
          aria-label={mode === 'profile' ? 'Profile workspace' : debouncedMainAriaLabel}
          style={{
            position: 'relative',
            minHeight: 0,
            overflow: 'hidden',
            background: mode === 'profile' ? tokens.color.canvas.light : canvasBackground,
          }}
        >
          {mode === 'profile' ? (
            <ProfileCanvas />
          ) : (
          <div className="map-stage" style={{ position: 'relative', width: '100%', height: '100%' }}>
            <div
              ref={mapContainer}
              id="map"
              role="application"
              tabIndex={0}
              aria-label={debouncedMainAriaLabel}
              aria-describedby={
                isPointCloudMode
                  ? 'pointcloud-description-region pointcloud-state-region'
                  : isThreeDMode
                    ? 'three-d-description-region three-d-state-region'
                    : 'map-description-region map-state-region'
              }
              onFocus={onMapFocus}
              onKeyDown={handleMapKeyDown}
              className="map-container"
              style={{
                width: '100%',
                height: '100%',
                background: canvasBackground,
              }}
            />

            {isPointCloudMode ? (
              <PointCloudPaneHost
                route={pointCloudRoute}
                defenceVariantActive={defenceVariant.active}
                ariaLabel={debouncedMainAriaLabel}
                description={debouncedCanvasDescription}
                stateNarration={debouncedStateNarration}
                onZoom={adjustZoom}
                onPan={panCamera}
                onOpenExternalViewer={() => openDrawer('help', null)}
                toolbar={(
                  <Stack direction="column" gap={1}>
                    <PointCloudColorMode
                      value={pointCloudColorMode}
                      onChange={setPointCloudColorMode}
                    />
                    <Stack direction="row" align="center" gap={2} style={{ flexWrap: 'wrap' }}>
                      <PointCloudSliceTool
                        offset={pointCloudSliceOffset}
                        onChange={setPointCloudSliceOffset}
                      />
                      <PointCloudDensityControls
                        densityTarget={pointCloudDensityTarget}
                        performance={pointCloudPerformance}
                        onDensityTargetChange={setPointCloudDensityTarget}
                      />
                    </Stack>
                  </Stack>
                )}
                bottomSlot={(
                  <Stack direction="row" align="center" gap={2} style={{ flexWrap: 'wrap' }}>
                    <ThreeDPerformanceChip snapshot={threeDPerformance} pointCloudMode />
                    <PointCloudRegistrationDiagnostics
                      registrationState={pointCloudRegistrationState}
                      onOpenAudit={() => {
                        setPointCloudRegistrationState('needs-review');
                        openDrawer('notifications', null);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => openDrawer('help', null)}
                      style={{
                        minHeight: tokens.density.touchMin,
                        border: tokens.border.thin,
                        borderRadius: tokens.radius.input,
                        background: tokens.color.surface[2],
                        color: tokens.color.ink.primary,
                        paddingInline: tokens.space[2],
                        cursor: 'pointer',
                      }}
                    >
                      Open Point Cloud Viewer
                    </button>
                  </Stack>
                )}
              >
                <div />
              </PointCloudPaneHost>
            ) : isThreeDMode ? (
              <ThreeDCanvasHost
                description={debouncedCanvasDescription}
                stateNarration={debouncedStateNarration}
              />
            ) : (
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <div
                  id="map-description-region"
                  role="region"
                  aria-label="Map description"
                  aria-live="polite"
                  aria-atomic="false"
                  style={{
                    position: 'absolute',
                    width: tokens.visuallyHidden.size,
                    height: tokens.visuallyHidden.size,
                    overflow: 'hidden',
                    clipPath: 'inset(50%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {debouncedCanvasDescription}
                </div>
                <div
                  id="map-state-region"
                  role="status"
                  aria-label="Map state narration"
                  aria-live="polite"
                  aria-atomic="false"
                  style={{
                    position: 'absolute',
                    width: tokens.visuallyHidden.size,
                    height: tokens.visuallyHidden.size,
                    overflow: 'hidden',
                    clipPath: 'inset(50%)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {debouncedStateNarration}
                </div>
              </div>
            )}

            {!isThreeDMode ? (
              <>
                <MapSearchResults
                  catalog={catalog}
                  onSelect={(layer) => flyToLayer(layer.id)}
                />
                <MapClusterOverlay
                  layers={activeLayers}
                  onSelectLayer={(layerId) => flyToLayer(layerId)}
                />
                <FloatingMapToolbar
                  activeTool={mapTool}
                  onToolChange={setMapTool}
                  onFitVisibleLayers={fitToVisibleLayers}
                  onOpenLayers={focusLayers}
                  onZoomIn={() => adjustZoom(1)}
                  onZoomOut={() => adjustZoom(-1)}
                />
                <MapStateOverlays
                  flags={mapFlags}
                  permissionDeniedMessage={permissionDeniedMessage}
                  onRetry={() => fitToVisibleLayers()}
                  onClearPermissionDenied={clearPermissionDeniedMessage}
                  onRequestAddLayer={focusLayers}
                />
              </>
            ) : null}

            {isThreeDMode && !isPointCloudMode ? (
              <>
                <ThreeDToolbar
                  activeMode={threeDCameraMode}
                  onModeChange={setThreeDCameraMode}
                  onResetView={fitToVisibleLayers}
                  onLookAtSelected={fitToVisibleLayers}
                  disableLookAtSelected={!selection}
                />
                <ThreeDCompass
                  headingDeg={camera?.hpr?.[0] ?? 0}
                  tiltDeg={camera?.hpr?.[1] ?? 0}
                  onResetNorth={handleResetNorth}
                />
                <ThreeDControlsPanel
                  hasSelection={Boolean(selection)}
                  isolated={threeDControlState.isolated}
                  hidden={threeDControlState.hidden}
                  ghosted={threeDControlState.ghosted}
                  sectionPlaneEnabled={threeDControlState.sectionPlaneEnabled}
                  clipBoxEnabled={threeDControlState.clipBoxEnabled}
                  exploded={threeDExplodeFactor}
                  lightingEnabled={threeDControlState.lightingEnabled}
                  shadowsEnabled={threeDControlState.shadowsEnabled}
                  sunHour={threeDControlState.sunHour}
                  sectionOffset={threeDControlState.sectionOffset}
                  clipOffset={threeDControlState.clipOffset}
                  materialState={threeDControlState.materialState}
                  canvasTone={effectiveThreeDCanvasTone}
                  canvasToneDisabled={!toneAvailable}
                  onToggleIsolate={() =>
                    setThreeDControlState((previous) => ({
                      ...previous,
                      isolated: !previous.isolated,
                    }))
                  }
                  onToggleHidden={() =>
                    setThreeDControlState((previous) => ({
                      ...previous,
                      hidden: !previous.hidden,
                    }))
                  }
                  onToggleGhosted={() =>
                    setThreeDControlState((previous) => ({
                      ...previous,
                      ghosted: !previous.ghosted,
                    }))
                  }
                  onToggleSectionPlane={() =>
                    setThreeDControlState((previous) => ({
                      ...previous,
                      sectionPlaneEnabled: !previous.sectionPlaneEnabled,
                    }))
                  }
                  onToggleClipBox={() =>
                    setThreeDControlState((previous) => ({
                      ...previous,
                      clipBoxEnabled: !previous.clipBoxEnabled,
                    }))
                  }
                  onChangeExploded={setThreeDExplodeFactor}
                  onToggleLighting={() =>
                    setThreeDControlState((previous) => ({
                      ...previous,
                      lightingEnabled: !previous.lightingEnabled,
                    }))
                  }
                  onToggleShadows={() =>
                    setThreeDControlState((previous) => ({
                      ...previous,
                      shadowsEnabled: !previous.shadowsEnabled,
                    }))
                  }
                  onChangeSunHour={(value) =>
                    setThreeDControlState((previous) => ({ ...previous, sunHour: value }))
                  }
                  onChangeSectionOffset={(value) =>
                    setThreeDControlState((previous) => ({
                      ...previous,
                      sectionOffset: value,
                    }))
                  }
                  onChangeClipOffset={(value) =>
                    setThreeDControlState((previous) => ({ ...previous, clipOffset: value }))
                  }
                  onToggleCanvasTone={() =>
                    setThreeDCanvasTone(
                      effectiveThreeDCanvasTone === 'neutral-dark' ? 'light' : 'neutral-dark',
                    )
                  }
                />
                <MissingGeometryDiagnostics
                  missingCount={missingGeometryCount}
                  onOpenRemap={() => openDrawer('help', null)}
                />
              </>
            ) : null}

            {isMayaFirstSession ? (
              <ShellWelcomeCard
                onDismiss={dismissWelcome}
                onShowMeAround={(opener) => openDrawer('help', opener)}
              />
            ) : null}
            <PluginDock />
          </div>
          )}
        </main>

        <ShellRightPanel
          engine={engine}
          selection={selection}
          selectedLayer={selectedLayer}
          mixedCrs={mapFlags.mixedCrs}
        />

        <div
          id="copilot"
          role="complementary"
          aria-label="Copilot"
          tabIndex={-1}
          style={{
            minHeight: 0,
            borderLeft: tokens.border.thin,
            background: tokens.color.surface[1],
          }}
        >
          <CopilotDock />
        </div>
      </div>

      <ShellBottomBar
        camera={camera}
        crs={crs}
        flags={mapFlags}
        supplementalStatus={supplementalBottomStatus}
        timeline={<TimelineScrubber />}
      />

      <ShellDrawers activeDrawer={activeDrawer} onClose={closeActiveDrawer} engine={engine} />
    </div>
  );
}
